import type { InterviewState, VerbatimEntry } from "./types";

export type ExportMeta = {
  appVersion: string;
  provider: string;
  locale: string;
  /** The operator-declared LLM_DATA_POLICY string (§13) — Fieldnote never asserts a training policy in its own voice; this carries forward exactly what was declared at collection time. */
  dataPolicy: string;
  timestamp: string;
};

export type ExportOptions = {
  meta: ExportMeta;
  /** The AI summary, if one was generated. Always isolated — see the note on ai_summary_not_participant_data below. */
  summary?: string | null;
};

/**
 * RFC-4180 escaping: wrap in quotes if the field contains a comma, a quote,
 * or a newline; double up any internal quotes. This is the whole rule —
 * resist the urge to "simplify" by only quoting commas, which silently
 * corrupts a value containing a bare newline or a literal `"`.
 */
export function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  return csvEscape(String(value));
}

function fieldValueForCsv(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map((v) => String(v)).join("; ");
  if (typeof value === "object" && "flag" in (value as Record<string, unknown>)) {
    return String((value as { flag: boolean }).flag);
  }
  return String(value);
}

function verbatimCell(entries: VerbatimEntry[] | null | undefined): string {
  if (!entries || entries.length === 0) return "";
  return entries.map((e) => `[turn ${e.turnIndex} · ${e.lang}] ${e.text}`).join("\n\n");
}

/**
 * One row. Per non-verbatim field: `<id>`, `<id>__source`, `<id>__quote`,
 * `<id>__confidence`, `<id>__reviewed`. Plus `notes_verbatim`,
 * `followup_needed`, `followup_reasons`. The AI summary goes ONLY in
 * `ai_summary_not_participant_data` — never mixed into an ordinary field
 * column, so a downstream reviewer can never mistake model prose for
 * something the participant said (§8, §13).
 */
export function toCsv(state: InterviewState, options: ExportOptions): string {
  const dataFields = state.template.fields.filter((f) => f.type !== "verbatim");

  const headers: string[] = [];
  for (const field of dataFields) {
    headers.push(field.id, `${field.id}__source`, `${field.id}__quote`, `${field.id}__confidence`, `${field.id}__reviewed`);
  }
  headers.push("notes_verbatim", "followup_needed", "followup_reasons", "ai_summary_not_participant_data");

  const row: string[] = [];
  for (const field of dataFields) {
    const fs = state.fields[field.id];
    row.push(
      csvCell(fieldValueForCsv(fs?.value)),
      csvCell(fs?.provenance?.source),
      csvCell(fs?.provenance?.quote),
      csvCell(fs?.provenance?.confidence),
      csvCell(fs?.provenance ? fs.provenance.reviewed : ""),
    );
  }

  const notesEntries = state.fields.notes_verbatim?.value as VerbatimEntry[] | null | undefined;
  row.push(
    csvCell(verbatimCell(notesEntries)),
    csvCell(state.followup.needed),
    csvCell(state.followup.reasons.join("; ")),
    csvCell(options.summary ?? ""),
  );

  return `${headers.map(csvEscape).join(",")}\r\n${row.join(",")}\r\n`;
}

/**
 * Full record: template, fields with provenance, transcript, safety
 * verdict, followup verdict, an `ai_generated` block, and `export_meta`
 * (§8). A pure function of state — no export path talks to the network or
 * mutates anything.
 */
export function toJsonRecord(state: InterviewState, options: ExportOptions): Record<string, unknown> {
  return {
    template: state.template,
    fields: state.fields,
    transcript: state.turns,
    safety: state.safety,
    followup: state.followup,
    ai_generated: {
      summary: options.summary ?? null,
      badge: "AI-generated — not participant data.",
    },
    export_meta: {
      app_version: options.meta.appVersion,
      provider: options.meta.provider,
      locale: options.meta.locale,
      timestamp: options.meta.timestamp,
      data_policy: options.meta.dataPolicy,
    },
  };
}

export function toJson(state: InterviewState, options: ExportOptions): string {
  return JSON.stringify(toJsonRecord(state, options), null, 2);
}
